import {removeRedundantWhiteSpaces} from "@pg-mono/string-utils";

import {PromotionType} from "./PromotionType";

export type PromotionListingType =
    | PromotionType.PROMOTION
    | PromotionType.OPEN_DAYS
    | PromotionType.DISCOUNT
    | PromotionType.LAST_MINUTE
    | PromotionType.PRESALE
    | PromotionType.NO_CONTRIBUTION;

interface IPromotionListingConfig {
    ctaLabel: string;
    description: string;
    fullLabel: string;
    getDefaultMessage: (offerName: string) => string;
    getHeading: (params: {monthYear: string; regionName?: string}) => string;
    getHeadingSuffix: (params: {monthYear: string; regionName?: string}) => string;
    headingHighlight: string;
    shortLabel: string;
    slug: string;
    type: PromotionListingType;
}

export const promotionListingTypes: PromotionListingType[] = [
    PromotionType.PROMOTION,
    PromotionType.OPEN_DAYS,
    PromotionType.DISCOUNT,
    PromotionType.LAST_MINUTE,
    PromotionType.PRESALE,
    PromotionType.NO_CONTRIBUTION
];

const appendRegion = (regionName?: string) => (regionName ? ` ${regionName}` : "");

const promotionListingConfig: Record<PromotionListingType, IPromotionListingConfig> = {
    [PromotionType.PROMOTION]: {
        type: PromotionType.PROMOTION,
        slug: "",
        shortLabel: "Promocje",
        fullLabel: "Promocje deweloperów",
        headingHighlight: "Promocje deweloperów",
        ctaLabel: "Zapytaj o promocję",
        description:
            "Aktualne promocje deweloperskie i okazje na rynku pierwotnym w jednym miejscu. Znajdziesz tu nowe mieszkania z rabatem, oferty z miejscem postojowym w cenie oraz pakiety wykończeniowe gratis. Porównaj oferty specjalne deweloperów i oszczędź na zakupie mieszkania!",
        getHeading: ({monthYear, regionName}) => `Promocje deweloperów - ${monthYear}${appendRegion(regionName)}`,
        getHeadingSuffix: ({monthYear, regionName}) => ` - ${monthYear}${appendRegion(regionName)}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o aktualnej promocji w inwestycji ${offerName}.`
    },
    [PromotionType.OPEN_DAYS]: {
        type: PromotionType.OPEN_DAYS,
        slug: "dni-otwarte",
        shortLabel: "Dni otwarte",
        fullLabel: "Dni otwarte",
        headingHighlight: "Dni otwarte u deweloperów",
        ctaLabel: "Zapytaj o dzień otwarty",
        description:
            "Zobacz harmonogram nadchodzących dni otwartych u deweloperów i odwiedź interesujące Cię inwestycje osobiście. To najlepsza okazja, aby obejrzeć pokazowe mieszkania, ocenić standard wykończenia na żywo i porozmawiać o bezpiecznym zakupie bezpośrednio z ekspertami w biurze sprzedaży.",
        getHeading: ({monthYear, regionName}) => `Dni otwarte u deweloperów${appendRegion(regionName)} - kalendarz wydarzeń ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} - kalendarz wydarzeń ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o dniach otwartych w inwestycji ${offerName}.`
    },
    [PromotionType.DISCOUNT]: {
        type: PromotionType.DISCOUNT,
        slug: "przeceny",
        shortLabel: "Przeceny",
        fullLabel: "Przeceny mieszkań",
        headingHighlight: "Przeceny i rabaty gotówkowe na nowe mieszkania",
        ctaLabel: "Zapytaj o rabat",
        description:
            "Przeglądaj mieszkania z rabatem gotówkowym i największe obniżki cen nieruchomości dostępne obecnie w sprzedaży. W tym miejscu zebraliśmy wyłącznie oferty z obniżoną ceną ofertową, dzięki czemu łatwo znajdziesz okazje inwestycyjne!",
        getHeading: ({monthYear, regionName}) => `Przeceny i rabaty gotówkowe na nowe mieszkania${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o przecenie mieszkania w inwestycji ${offerName}.`
    },
    [PromotionType.LAST_MINUTE]: {
        type: PromotionType.LAST_MINUTE,
        slug: "last-minute",
        shortLabel: "Last Minute",
        fullLabel: "Oferty Last Minute",
        headingHighlight: "Last Minute",
        ctaLabel: "Zapytaj o ofertę Last Minute",
        description:
            "Polujesz na ostatnie wolne mieszkania w inwestycji? Sprawdź ofertę Last Minute i końcówki sprzedaży deweloperskiej! To Twoja szansa na zakup gotowego lokalu i szybką przeprowadzkę, często z możliwością negocjacji ceny za ostatnie sztuki.",
        getHeading: ({monthYear, regionName}) => `Last Minute: ostatnie mieszkania w niższych cenach${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `: ostatnie mieszkania w niższych cenach${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o ofercie Last Minute w inwestycji ${offerName}.`
    },
    [PromotionType.PRESALE]: {
        type: PromotionType.PRESALE,
        slug: "przedsprzedaz",
        shortLabel: "Przedsprzedaż",
        fullLabel: "Przedsprzedaż",
        headingHighlight: "Przedsprzedaż nowych inwestycji",
        ctaLabel: "Zapytaj o przedsprzedaż",
        description:
            "Zyskaj dostęp do przedsprzedaży nowych inwestycji i rezerwuj mieszkania w najniższych cenach na start budowy! Wybierając mieszkanie w przedsprzedaży, masz największy wybór rozkładów i pięter.",
        getHeading: ({monthYear, regionName}) => `Przedsprzedaż nowych inwestycji - okazje na start${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => ` - okazje na start${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o przedsprzedaży mieszkań w inwestycji ${offerName}.`
    },
    [PromotionType.NO_CONTRIBUTION]: {
        type: PromotionType.NO_CONTRIBUTION,
        slug: "bez-wkladu-wlasnego",
        shortLabel: "Bez wkładu własnego",
        fullLabel: "Mieszkania bez wkładu własnego",
        headingHighlight: "Mieszkania bez wkładu własnego",
        ctaLabel: "Zapytaj o ofertę",
        description:
            'Szukasz sposobu na zakup pierwszej nieruchomości, ale nie masz żadnych oszczędności? W tej sekcji znajdziesz nowe mieszkania bez wkładu własnego, które kwalifikują się do rządowych programów wsparcia i gwarancji BGK. Przeglądaj inwestycje deweloperskie spełniające aktualne limity cenowe, skorzystaj z opcji finansowania 100% i zamieszkaj "na swoim" bez konieczności długoletniego odkładania gotówki.',
        getHeading: ({monthYear, regionName}) => `Mieszkania bez wkładu własnego${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o mieszkaniu bez wkładu własnego w inwestycji ${offerName}.`
    }
};

export const isPromotionListingType = (type?: number): type is PromotionListingType => promotionListingTypes.includes(type as PromotionListingType);

export const getPromotionListingType = (type?: PromotionType | number | null): PromotionListingType => {
    return isPromotionListingType(type ?? undefined) ? (type as PromotionListingType) : PromotionType.PROMOTION;
};

export const getPromotionListingConfig = (type?: PromotionType | number | null): IPromotionListingConfig => {
    return promotionListingConfig[getPromotionListingType(type)];
};

export const getPromotionListingMonthYear = (date = new Date()): string => {
    return date.toLocaleString("pl-PL", {month: "long", year: "numeric"});
};

export const getPromotionListingHeading = (params: {date?: Date; regionName?: string; type?: PromotionType | number | null}): string => {
    const config = getPromotionListingConfig(params.type);

    return removeRedundantWhiteSpaces(
        config.getHeading({
            monthYear: getPromotionListingMonthYear(params.date),
            regionName: params.regionName
        })
    );
};

export const getPromotionListingDescription = (type?: PromotionType | number | null): string => {
    return getPromotionListingConfig(type).description;
};

export const getPromotionListingDefaultMessage = (type: PromotionType | number | null | undefined, offerName: string): string => {
    return getPromotionListingConfig(type).getDefaultMessage(offerName);
};

export const getPromotionListingHeadingHighlight = (type?: PromotionType | number | null): string => {
    return getPromotionListingConfig(type).headingHighlight;
};

export const getPromotionListingHeadingSuffix = (params: {date?: Date; regionName?: string; type?: PromotionType | number | null}): string => {
    const config = getPromotionListingConfig(params.type);

    return removeRedundantWhiteSpaces(
        config.getHeadingSuffix({
            monthYear: getPromotionListingMonthYear(params.date),
            regionName: params.regionName
        })
    );
};

export const getPromotionListingSlugByType = (type?: PromotionType): string => {
    if (!isPromotionListingType(type)) {
        return "";
    }

    return getPromotionListingConfig(type).slug;
};
import {removeRedundantWhiteSpaces} from "@pg-mono/string-utils";

import {PromotionType} from "./PromotionType";

export type PromotionListingType =
    | PromotionType.PROMOTION
    | PromotionType.OPEN_DAYS
    | PromotionType.DISCOUNT
    | PromotionType.LAST_MINUTE
    | PromotionType.PRESALE
    | PromotionType.NO_CONTRIBUTION;

interface IPromotionListingConfig {
    ctaLabel: string;
    description: string;
    fullLabel: string;
    getDefaultMessage: (offerName: string) => string;
    getHeading: (params: {monthYear: string; regionName?: string}) => string;
    getHeadingSuffix: (params: {monthYear: string; regionName?: string}) => string;
    headingHighlight: string;
    shortLabel: string;
    slug: string;
    type: PromotionListingType;
}

export const promotionListingTypes: PromotionListingType[] = [
    PromotionType.PROMOTION,
    PromotionType.OPEN_DAYS,
    PromotionType.DISCOUNT,
    PromotionType.LAST_MINUTE,
    PromotionType.PRESALE,
    PromotionType.NO_CONTRIBUTION
];

const appendRegion = (regionName?: string) => (regionName ? ` ${regionName}` : "");

const promotionListingConfig: Record<PromotionListingType, IPromotionListingConfig> = {
    [PromotionType.PROMOTION]: {
        type: PromotionType.PROMOTION,
        slug: "",
        shortLabel: "Promocje",
        fullLabel: "Promocje deweloperów",
        headingHighlight: "Promocje deweloperów",
        ctaLabel: "Zapytaj o promocję",
        description:
            "Aktualne promocje deweloperskie i okazje na rynku pierwotnym w jednym miejscu. Znajdziesz tu nowe mieszkania z rabatem, oferty z miejscem postojowym w cenie oraz pakiety wykończeniowe gratis. Porównaj oferty specjalne deweloperów i oszczędź na zakupie mieszkania!",
        getHeading: ({monthYear, regionName}) => `Promocje deweloperów - ${monthYear}${appendRegion(regionName)}`,
        getHeadingSuffix: ({monthYear, regionName}) => ` - ${monthYear}${appendRegion(regionName)}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o aktualnej promocji w inwestycji ${offerName}.`
    },
    [PromotionType.OPEN_DAYS]: {
        type: PromotionType.OPEN_DAYS,
        slug: "dni-otwarte",
        shortLabel: "Dni otwarte",
        fullLabel: "Dni otwarte",
        headingHighlight: "Dni otwarte u deweloperów",
        ctaLabel: "Zapytaj o dzień otwarty",
        description:
            "Zobacz harmonogram nadchodzących dni otwartych u deweloperów i odwiedź interesujące Cię inwestycje osobiście. To najlepsza okazja, aby obejrzeć pokazowe mieszkania, ocenić standard wykończenia na żywo i porozmawiać o bezpiecznym zakupie bezpośrednio z ekspertami w biurze sprzedaży.",
        getHeading: ({monthYear, regionName}) => `Dni otwarte u deweloperów${appendRegion(regionName)} - kalendarz wydarzeń ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} - kalendarz wydarzeń ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o dniach otwartych w inwestycji ${offerName}.`
    },
    [PromotionType.DISCOUNT]: {
        type: PromotionType.DISCOUNT,
        slug: "przeceny",
        shortLabel: "Przeceny",
        fullLabel: "Przeceny mieszkań",
        headingHighlight: "Przeceny i rabaty gotówkowe na nowe mieszkania",
        ctaLabel: "Zapytaj o rabat",
        description:
            "Przeglądaj mieszkania z rabatem gotówkowym i największe obniżki cen nieruchomości dostępne obecnie w sprzedaży. W tym miejscu zebraliśmy wyłącznie oferty z obniżoną ceną ofertową, dzięki czemu łatwo znajdziesz okazje inwestycyjne!",
        getHeading: ({monthYear, regionName}) => `Przeceny i rabaty gotówkowe na nowe mieszkania${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o przecenie mieszkania w inwestycji ${offerName}.`
    },
    [PromotionType.LAST_MINUTE]: {
        type: PromotionType.LAST_MINUTE,
        slug: "last-minute",
        shortLabel: "Last Minute",
        fullLabel: "Oferty Last Minute",
        headingHighlight: "Last Minute",
        ctaLabel: "Zapytaj o ofertę Last Minute",
        description:
            "Polujesz na ostatnie wolne mieszkania w inwestycji? Sprawdź ofertę Last Minute i końcówki sprzedaży deweloperskiej! To Twoja szansa na zakup gotowego lokalu i szybką przeprowadzkę, często z możliwością negocjacji ceny za ostatnie sztuki.",
        getHeading: ({monthYear, regionName}) => `Last Minute: ostatnie mieszkania w niższych cenach${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `: ostatnie mieszkania w niższych cenach${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o ofercie Last Minute w inwestycji ${offerName}.`
    },
    [PromotionType.PRESALE]: {
        type: PromotionType.PRESALE,
        slug: "przedsprzedaz",
        shortLabel: "Przedsprzedaż",
        fullLabel: "Przedsprzedaż",
        headingHighlight: "Przedsprzedaż nowych inwestycji",
        ctaLabel: "Zapytaj o przedsprzedaż",
        description:
            "Zyskaj dostęp do przedsprzedaży nowych inwestycji i rezerwuj mieszkania w najniższych cenach na start budowy! Wybierając mieszkanie w przedsprzedaży, masz największy wybór rozkładów i pięter.",
        getHeading: ({monthYear, regionName}) => `Przedsprzedaż nowych inwestycji - okazje na start${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => ` - okazje na start${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o przedsprzedaży mieszkań w inwestycji ${offerName}.`
    },
    [PromotionType.NO_CONTRIBUTION]: {
        type: PromotionType.NO_CONTRIBUTION,
        slug: "bez-wkladu-wlasnego",
        shortLabel: "Bez wkładu własnego",
        fullLabel: "Mieszkania bez wkładu własnego",
        headingHighlight: "Mieszkania bez wkładu własnego",
        ctaLabel: "Zapytaj o ofertę",
        description:
            'Szukasz sposobu na zakup pierwszej nieruchomości, ale nie masz żadnych oszczędności? W tej sekcji znajdziesz nowe mieszkania bez wkładu własnego, które kwalifikują się do rządowych programów wsparcia i gwarancji BGK. Przeglądaj inwestycje deweloperskie spełniające aktualne limity cenowe, skorzystaj z opcji finansowania 100% i zamieszkaj "na swoim" bez konieczności długoletniego odkładania gotówki.',
        getHeading: ({monthYear, regionName}) => `Mieszkania bez wkładu własnego${appendRegion(regionName)} ${monthYear}`,
        getHeadingSuffix: ({monthYear, regionName}) => `${appendRegion(regionName)} ${monthYear}`,
        getDefaultMessage: (offerName) => `Dzień dobry, proszę o więcej informacji o mieszkaniu bez wkładu własnego w inwestycji ${offerName}.`
    }
};

export const isPromotionListingType = (type?: number): type is PromotionListingType => promotionListingTypes.includes(type as PromotionListingType);

export const getPromotionListingType = (type?: PromotionType | number | null): PromotionListingType => {
    return isPromotionListingType(type ?? undefined) ? (type as PromotionListingType) : PromotionType.PROMOTION;
};

export const getPromotionListingConfig = (type?: PromotionType | number | null): IPromotionListingConfig => {
    return promotionListingConfig[getPromotionListingType(type)];
};

export const getPromotionListingMonthYear = (date = new Date()): string => {
    return date.toLocaleString("pl-PL", {month: "long", year: "numeric"});
};

export const getPromotionListingHeading = (params: {date?: Date; regionName?: string; type?: PromotionType | number | null}): string => {
    const config = getPromotionListingConfig(params.type);

    return removeRedundantWhiteSpaces(
        config.getHeading({
            monthYear: getPromotionListingMonthYear(params.date),
            regionName: params.regionName
        })
    );
};

export const getPromotionListingDescription = (type?: PromotionType | number | null): string => {
    return getPromotionListingConfig(type).description;
};

export const getPromotionListingDefaultMessage = (type: PromotionType | number | null | undefined, offerName: string): string => {
    return getPromotionListingConfig(type).getDefaultMessage(offerName);
};

export const getPromotionListingHeadingHighlight = (type?: PromotionType | number | null): string => {
    return getPromotionListingConfig(type).headingHighlight;
};

export const getPromotionListingHeadingSuffix = (params: {date?: Date; regionName?: string; type?: PromotionType | number | null}): string => {
    const config = getPromotionListingConfig(params.type);

    return removeRedundantWhiteSpaces(
        config.getHeadingSuffix({
            monthYear: getPromotionListingMonthYear(params.date),
            regionName: params.regionName
        })
    );
};

export const getPromotionListingSlugByType = (type?: PromotionType): string => {
    if (!isPromotionListingType(type)) {
        return "";
    }

    return getPromotionListingConfig(type).slug;
};
